"use client";

import { motion } from "framer-motion";
import {
  Icon20ArrowRightOutline,
  Icon20Stars,
  Icon20FireAltOutline,
  Icon20SparkleOutline,
} from "@linqx/icons/web";
import { Language } from "@linqx/i18n";
import { Reveal } from "@/components/motion/scroll-fx";
import { Magnet } from "@/components/reactbits/magnet";
import { Logo } from "@/components/logo";
import { scrollToId } from "@/lib/scroll";
import { Telemetry, TelemetryEvent } from "@linqx/telemetry";

export function CTA() {
  return (
    <section
      id="about"
      className="relative flex min-h-screen items-center overflow-hidden bg-background py-32 md:py-44"
    >
      {/* Single subtle centered glow for depth */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/2 top-1/2 h-[640px] w-[640px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-100/40 blur-[150px]" />
      </div>

      <div className="container relative z-10 mx-auto px-4">
        <div className="mx-auto max-w-3xl text-center">
          {/* Logo */}
          <Reveal
            direction="scale"
            distance={0}
            className="mb-8 flex justify-center"
          >
            <motion.div
              animate={{ rotate: [0, 5, -5, 0], y: [0, -6, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              whileHover={{ scale: 1.08, rotate: 0 }}
              className="rounded-2xl border border-border bg-background p-4 shadow-lg shadow-brand-900/5"
            >
              <Logo size={40} />
            </motion.div>
          </Reveal>

          {/* Heading */}
          <Reveal direction="up" distance={56}>
            <h2 className="mb-4 font-heading text-3xl font-bold md:text-5xl">
              {Language.getKey("landing_cta_title_prefix")}
              <span className="text-brand-600">
                {Language.getKey("landing_cta_title_highlight")}
              </span>
            </h2>
          </Reveal>

          <Reveal direction="up" distance={48} delay={0.04}>
            <p className="mb-12 text-base text-muted-foreground md:text-lg">
              {Language.getKey("landing_cta_subtitle")}
            </p>
          </Reveal>

          {/* Three pillars — fly up from below, staggered, with idle float */}
          <div className="mb-12 flex justify-center gap-8 md:gap-14">
            {[
              {
                icon: Icon20Stars,
                labelKey: "landing_cta_pillar_swipe",
                delay: 0.06,
                float: 7,
              },
              {
                icon: Icon20FireAltOutline,
                labelKey: "landing_cta_pillar_link",
                delay: 0.12,
                float: 9,
              },
              {
                icon: Icon20SparkleOutline,
                labelKey: "landing_cta_pillar_live",
                delay: 0.18,
                float: 6,
              },
            ].map(({ icon: Icon, labelKey, delay, float }, i) => (
              <Reveal key={labelKey} direction="up" distance={72} delay={delay}>
                <motion.div
                  animate={{ y: [0, -float, 0] }}
                  transition={{
                    duration: 3.5 + i * 0.4,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: i * 0.3,
                  }}
                  whileHover={{ scale: 1.12 }}
                  className="flex flex-col items-center gap-2"
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-full border border-border bg-background shadow-md shadow-brand-900/5">
                    <Icon className="text-brand-600" />
                  </div>
                  <span className="text-sm font-medium text-foreground">
                    {Language.getKey(labelKey)}
                  </span>
                </motion.div>
              </Reveal>
            ))}
          </div>

          {/* CTA Button */}
          <Reveal direction="scale" distance={0} delay={0.22}>
            <Magnet padding={70} magnetStrength={3.5}>
              <motion.button
                whileHover={{ scale: 1.06, y: -2 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: "spring", stiffness: 400, damping: 18 }}
                onClick={() => {
                  Telemetry.track(TelemetryEvent.CTA_CLICKED, {
                    location: "cta_section",
                    cta: "get_started",
                  });
                  scrollToId("waitlist");
                }}
                className="btn-brand group inline-flex h-12 items-center gap-2 rounded-full px-8 text-base font-semibold shadow-lg shadow-brand-900/10"
              >
                {Language.getKey("landing_cta_button")}
                <Icon20ArrowRightOutline className="transition-transform group-hover:translate-x-1" />
              </motion.button>
            </Magnet>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
